import type { GetApiPayloadByUrl } from '@crossbeam/openapi';

import { computed, inject, ref } from 'vue';
import { useRoute } from 'vue-router';

import { RECORD_ID_QUERY_PARAM } from '@/components/sales/AccountDrawer/constants';

import { crossbeamApi } from '@/api';
import {
  ProvidedDealNavData,
  ProvidedIsGenPipe,
} from '@/injectionKeys/deal_navigator';

type ActivityPayload = GetApiPayloadByUrl<'/v0.1/activities', 'post'>;
type ActivityType = ActivityPayload['type'];

export function useDealNavActivities() {
  const route = useRoute();
  const isGenPipe = inject(ProvidedIsGenPipe, ref(false));
  const dealNavData = inject(ProvidedDealNavData, ref([]));

  async function trackDealNavActivity(
    type: ActivityType,
    recordId?: string,
    context?: {
      partner_organization_uuid?: string;
      partner_organization_uuids?: string[];
      contact_name?: string;
      contact_email?: string;
    },
  ) {
    // Only track activities when user is on deal navigator pages
    if (!route.path.startsWith('/deal-navigator')) return;

    const masterId = computed(
      () => recordId ?? route.query[RECORD_ID_QUERY_PARAM]?.toString() ?? '',
    );

    const currentRecord = computed(
      () =>
        dealNavData.value?.find((record) =>
          isGenPipe.value
            ? record.master_id === masterId.value
            : record.record_id === masterId.value,
        ) || null,
    );

    const sourceId = computed(() => {
      const sourceId = isGenPipe.value
        ? currentRecord.value?.source_id
        : currentRecord.value?.master_source_id;
      return sourceId ?? 0;
    });

    const basePayload = {
      activity_source: isGenPipe.value ? 'gen-pipeline' : 'deal-nav',
      master_id: masterId.value,
      source_id: sourceId.value,
      timestamp: new Date().toISOString(),
      type,
      data: {},
    } as ActivityPayload;

    let payload: ActivityPayload;

    switch (type) {
      case 'user_clicked_linkedin':
      case 'user_copied_email':
        payload = {
          ...basePayload,
          data: {
            partner_organization_uuids:
              context?.partner_organization_uuids ?? [],
          },
        };
        break;
      case 'user_added_contact_to_crm':
        payload = {
          ...basePayload,
          data: {
            contact_name: context?.contact_name ?? '',
            contact_email: context?.contact_email ?? '',
          },
        };
        break;
      case 'user_clicked_send_message':
        payload = {
          ...basePayload,
          partner_organization_uuid: context?.partner_organization_uuid,
        };
        break;
      case 'user_opened_account_in_crm':
        if (!isGenPipe.value) return;
        payload = {
          ...basePayload,
          data: { partner_organization_uuids: [] },
        };
        break;
      default:
        payload = basePayload;
        break;
    }

    await crossbeamApi.POST('/v0.1/activities', {
      body: payload,
    });
  }

  return {
    trackDealNavActivity,
  };
}
